import React from 'react'
import { isEditableEventTarget } from '../../helpers/editable-event-target'
import { useClipboardCommands } from '../use-clipboard-commands'
import { useClipboardPaste } from '../use-clipboard-paste'
import { useDeleteSelection } from '../use-delete-selection'
import { useEditModeKeyCapture } from '../use-edit-mode-key-capture'
import { usePenEditHoverReset } from '../use-pen-edit-hover-reset'
import { usePhotoActionBar } from '../use-photo-action-bar'
import { useShapeDoubleClick } from '../use-shape-double-click'
import { useTextCreationSelectionReset } from '../use-text-creation-selection-reset'
import { useTextEditRouting } from '../use-text-edit-routing'
import { useToolModeSelectionReset } from '../use-tool-mode-selection-reset'
import type {
  CanvasEditModes,
  CanvasFoundation,
  CanvasTextLinking,
  CanvasToolModes,
  CanvasViewport,
} from '../../types/canvas-groups'
import type { ResolvedCenterCanvasConfig } from '../../types/resolved-center-canvas-config'

/**
 * Commands that act on the current selection — copy, cut, paste, delete, routed to the active
 * text editor when one is open — plus the photo action bar, double-click to enter an edit mode,
 * the selection resets when a creation tool takes over, and the edit modes' Escape/Enter capture.
 */
export function useCanvasSelectionCommands(
  {
    onConvertShapeToPath,
    onShapeCommitted,
    onUnifiedLayerArrange,
    activeItem,
    onDeleteGestureScope,
    onShapeSelectionHistory,
    onEditTextPath,
    onDeleteShapesHandled,
    onAddCaption,
    onRemovePhotoFromShape,
    onRemoveBackgroundFromShape,
    isRemovingBackgroundShapeId,
    photoEffectsOpen,
    onPhotoEffectsOpenChange,
    isTextToolActive,
  }: ResolvedCenterCanvasConfig,
  {
    newShapeTextSeedStyle,
    internalShapeManagerRef,
    unifiedLayerIdsRef,
    linkedTextChainsRef,
    scrollRef,
    isShapeTextEditing,
  }: Pick<
    CanvasFoundation,
    | 'newShapeTextSeedStyle'
    | 'internalShapeManagerRef'
    | 'unifiedLayerIdsRef'
    | 'linkedTextChainsRef'
    | 'scrollRef'
    | 'isShapeTextEditing'
  >,
  { scale, getCanvasOrigin }: Pick<CanvasViewport, 'scale' | 'getCanvasOrigin'>,
  { handleSelectChainMembers }: Pick<CanvasTextLinking, 'handleSelectChainMembers'>,
  {
    isInShapeCreationMode,
    isInPenToolMode,
    isInTextCreationMode,
  }: Pick<CanvasToolModes, 'isInShapeCreationMode' | 'isInPenToolMode' | 'isInTextCreationMode'>,
  {
    nodeEditShapeId,
    nodeEditShapeIdRef,
    nodeEditToolbarRef,
    isNodeEditMode,
    isPenEditMode,
    setPenEditHoverKind,
    exitNodeEditMode,
    cropShapeId,
    setCropShapeData,
    cropGestureBeforeRef,
    cropOverlayRef,
    cropZoomSliderRef,
    cropToolbarRef,
    isCropMode,
    exitCropMode,
    textOnPathToolbarRef,
    isTextOnPathMode,
    selectedTextBoxIdsRef,
    selectedShapeIdsForUnified,
    clearShapeSelectionWithHistory,
    enterGroupIsolationMode,
    activeIsolationGroupId,
    pulseSelectionGeometryBlocked,
    tryEnterNodeEditMode,
    tryEnterCropMode,
    commitAndExitTextOnPathMode,
    tryEnterTextOnPathMode,
    canSelectShapes,
  }: Pick<
    CanvasEditModes,
    | 'nodeEditShapeId'
    | 'nodeEditShapeIdRef'
    | 'nodeEditToolbarRef'
    | 'isNodeEditMode'
    | 'isPenEditMode'
    | 'setPenEditHoverKind'
    | 'exitNodeEditMode'
    | 'cropShapeId'
    | 'setCropShapeData'
    | 'cropGestureBeforeRef'
    | 'cropOverlayRef'
    | 'cropZoomSliderRef'
    | 'cropToolbarRef'
    | 'isCropMode'
    | 'exitCropMode'
    | 'textOnPathToolbarRef'
    | 'isTextOnPathMode'
    | 'selectedTextBoxIdsRef'
    | 'selectedShapeIdsForUnified'
    | 'clearShapeSelectionWithHistory'
    | 'enterGroupIsolationMode'
    | 'activeIsolationGroupId'
    | 'pulseSelectionGeometryBlocked'
    | 'tryEnterNodeEditMode'
    | 'tryEnterCropMode'
    | 'commitAndExitTextOnPathMode'
    | 'tryEnterTextOnPathMode'
    | 'canSelectShapes'
  >
) {
  const {
    clipboardHistoryRef,
    getCurrentSelectionSnapshots,
    buildDeleteGestureScope,
    canUseClipboardEntry,
    hasPasteCandidate,
    copySelectionToClipboard,
    cutSelectionToClipboard,
  } = useClipboardCommands({
    internalShapeManagerRef,
    linkedTextChainsRef,
    unifiedLayerIdsRef,
    activeIsolationGroupId,
    pulseSelectionGeometryBlocked,
    onDeleteGestureScope,
    onDeleteShapesHandled,
  })

  const { pasteFromClipboard } = useClipboardPaste({
    internalShapeManagerRef,
    scrollRef,
    getCanvasOrigin,
    scale,
    clipboardHistoryRef,
    canUseClipboardEntry,
    onShapeSelectionHistory,
    onUnifiedLayerArrange,
  })

  const {
    cutSelectionForActiveMode,
    isTextEditSessionActive,
    refocusActiveTextEditor,
    selectAllForActiveTextEdit,
    deleteSelectionForActiveMode,
    handleDeleteKeyWhileTextEditing,
  } = useTextEditRouting({
    internalShapeManagerRef,
    cutSelectionToClipboard,
    handleSelectChainMembers,
  })

  const { deleteSelectedObjects } = useDeleteSelection({
    internalShapeManagerRef,
    canSelectShapes,
    activeIsolationGroupId,
    isTextEditSessionActive,
    getCurrentSelectionSnapshots,
    buildDeleteGestureScope,
    pulseSelectionGeometryBlocked,
    onDeleteGestureScope,
  })

  const photoActionBar = usePhotoActionBar({
    internalShapeManagerRef,
    activeItem,
    isCropMode,
    isNodeEditMode,
    isTextOnPathMode,
    isShapeTextEditing,
    selectedShapeIdsForUnified,
    tryEnterCropMode,
    isRemovingBackgroundShapeId,
    photoEffectsOpen,
    onPhotoEffectsOpenChange,
    onEditTextPath,
    onRemoveBackgroundFromShape,
    onAddCaption,
    onRemovePhotoFromShape,
    onShapeCommitted,
  })

  const { handleShapeDoubleClick } = useShapeDoubleClick({
    internalShapeManagerRef,
    isTextToolActive,
    newShapeTextSeedStyle,
    enterGroupIsolationMode,
    tryEnterTextOnPathMode,
    tryEnterCropMode,
    tryEnterNodeEditMode,
    onConvertShapeToPath,
  })

  const isEditableTarget = React.useCallback(
    (target: EventTarget | null) => isEditableEventTarget(target),
    []
  )

  useToolModeSelectionReset({
    internalShapeManagerRef,
    isInShapeCreationMode,
    isInPenToolMode,
    isPenEditMode,
    isNodeEditMode,
    nodeEditShapeIdRef,
    clearShapeSelectionWithHistory,
    tryEnterNodeEditMode,
  })

  useEditModeKeyCapture({
    internalShapeManagerRef,
    isEditableTarget,
    isNodeEditMode,
    nodeEditShapeId,
    nodeEditToolbarRef,
    exitNodeEditMode,
    isTextOnPathMode,
    textOnPathToolbarRef,
    commitAndExitTextOnPathMode,
    isCropMode,
    cropShapeId,
    setCropShapeData,
    cropOverlayRef,
    cropZoomSliderRef,
    cropToolbarRef,
    cropGestureBeforeRef,
    exitCropMode,
  })

  usePenEditHoverReset({ isPenEditMode, setPenEditHoverKind })

  useTextCreationSelectionReset({
    internalShapeManagerRef,
    isInTextCreationMode,
    selectedTextBoxIdsRef,
    clearShapeSelectionWithHistory,
  })

  return {
    getCurrentSelectionSnapshots,
    hasPasteCandidate,
    copySelectionToClipboard,
    cutSelectionToClipboard,
    pasteFromClipboard,
    cutSelectionForActiveMode,
    isTextEditSessionActive,
    refocusActiveTextEditor,
    selectAllForActiveTextEdit,
    deleteSelectionForActiveMode,
    handleDeleteKeyWhileTextEditing,
    deleteSelectedObjects,
    handleShapeDoubleClick,
    photoActionBar,
    isEditableTarget,
  }
}
